// The gates read this. Nothing else does.
//
// Rule inherited from SESSION_STATE.md: an instrument must never supply, or stand in for, the thing
// it observes. So this module NEVER computes a verdict -- it only publishes raw counts that the
// harness compares against thresholds it holds itself. If the pass/fail lived here, a broken build
// could report its own health.

const state = {
  ready: false,
  bootError: null,
  errors: [],          // console errors + uncaught rejections, captured verbatim
  warnings: [],        // console.warn, captured verbatim. Gated as "no NEW warnings vs baseline",
                       // never "zero" -- three.js warns in normal operation and a zero-warning gate
                       // only teaches you to suppress them.
  buildMs: null,       // wall clock, first generator call to last
  heapMB: null,        // JS heap after generation settles
  pixelRatio: null,    // PINNED for measurement; adaptive scaling would hide every regression
  frames: 0,
  frameMs: [],         // rolling window of rAF deltas -- VSYNC-CAPPED, do not gate on this alone
  renderMs: [],        // time inside renderer.render(), which is the number that actually moves
  drawCalls: 0,
  triangles: 0,
  programs: 0,
  geometries: 0,
  textures: 0,
  system: null,        // which system last touched the build
  cameraStation: null,
  playerPos: [0, 0, 0],
  events: {},          // named counters -- 'fauna.headTurn', 'robot.telegraph', ...
};

if (typeof window !== 'undefined') {
  window.__NP = state;

  window.addEventListener('error', (e) => {
    state.errors.push({ kind: 'error', msg: String(e.message), src: e.filename, line: e.lineno });
  });
  window.addEventListener('unhandledrejection', (e) => {
    state.errors.push({ kind: 'rejection', msg: String(e.reason && e.reason.message || e.reason) });
  });

  // Console errors are separate from window.onerror -- a caught-and-logged failure never reaches
  // the error event, and the brief's gate says ZERO console errors.
  const realError = console.error.bind(console);
  console.error = (...args) => {
    state.errors.push({ kind: 'console', msg: args.map(String).join(' ') });
    realError(...args);
  };
  const realWarn = console.warn.bind(console);
  console.warn = (...args) => {
    state.warnings.push(args.map(String).join(' '));
    realWarn(...args);
  };
}

export function markReady(system) {
  state.system = system;
  state.ready = true;
}

/** Wall clock across the whole geometry build. Called once, either side of generation. */
export function buildClock(startMs) {
  state.buildMs = +(performance.now() - startMs).toFixed(1);
  if (performance.memory) state.heapMB = +(performance.memory.usedJSHeapSize / 1048576).toFixed(1);
}

export function markBootError(err) {
  state.bootError = String((err && err.stack) || err);
  state.errors.push({ kind: 'boot', msg: state.bootError });
}

export function frame(dtMs, renderer, renderMs) {
  state.frames++;
  state.frameMs.push(dtMs);
  if (state.frameMs.length > 240) state.frameMs.shift();
  if (renderMs != null) {
    state.renderMs.push(renderMs);
    if (state.renderMs.length > 240) state.renderMs.shift();
  }
  if (renderer) {
    const i = renderer.info;
    state.drawCalls = i.render.calls;
    state.triangles = i.render.triangles;
    state.programs = i.programs ? i.programs.length : 0;
    state.geometries = i.memory.geometries;
    state.textures = i.memory.textures;
  }
}

export function count(name, by = 1) {
  state.events[name] = (state.events[name] || 0) + by;
}

export function setPlayer(pos, station) {
  state.playerPos = [+pos.x.toFixed(3), +pos.y.toFixed(3), +pos.z.toFixed(3)];
  if (station != null) state.cameraStation = station;
}

export default state;
